import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  CreditCard, 
  Calendar, 
  CheckCircle, 
  Loader2,
  Sparkles,
  User,
  Building2,
  Camera,
  Save,
  Facebook,
  Instagram,
  Linkedin,
  Twitter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Account = () => {
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [usage, setUsage] = useState({ active_listings: 0, listing_limit: 10 });
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const headshotInputRef = useRef(null);
  const logoInputRef = useRef(null);
  
  // Agent profile state
  const [profile, setProfile] = useState({
    name: '',
    phone: '',
    brokerage_name: '',
    social_facebook: '',
    social_instagram: '',
    social_linkedin: '',
    social_twitter: ''
  });
  const [headshot, setHeadshot] = useState(null);
  const [brokerageLogo, setBrokerageLogo] = useState(null);

  useEffect(() => {
    fetchData();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    console.log('Account loaded - sessionId:', sessionId, 'success:', success);
    
    if (sessionId && success === 'true') {
      console.log('Starting payment status polling for session:', sessionId);
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);
  
  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        phone: user.phone || '',
        brokerage_name: user.brokerage_name || '',
        social_facebook: user.social_facebook || '',
        social_instagram: user.social_instagram || '',
        social_linkedin: user.social_linkedin || '',
        social_twitter: user.social_twitter || ''
      });
      setHeadshot(user.headshot_photo);
      setBrokerageLogo(user.brokerage_logo);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [usageRes, historyRes] = await Promise.all([
        axios.get(`${API}/usage/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/billing/history`, { headers: getAuthHeaders() })
      ]);
      setUsage(usageRes.data);
      setBillingHistory(historyRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15;  // Increased attempts
    
    console.log(`Polling payment status - attempt ${attempts + 1}/${maxAttempts}`);
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      // Clear URL params
      window.history.replaceState({}, '', '/account');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/billing/status/${sessionId}`,
        { headers: getAuthHeaders() }
      );
      
      console.log('Payment status response:', response.data);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        // Clear URL params
        window.history.replaceState({}, '', '/account');
        // Force refresh user data
        await refreshUser();
        await fetchData();
        // Force page reload to ensure all state is updated
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }
      
      if (response.data.status === 'complete' && response.data.payment_status !== 'paid') {
        // Session complete but not paid - something is wrong
        console.error('Session complete but payment not marked as paid');
      }
      
      // Continue polling with increasing delay
      const delay = Math.min(2000 + (attempts * 500), 5000);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), delay);
    } catch (error) {
      console.error('Payment status error:', error.response?.data || error);
      // Continue polling on error
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 3000);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const response = await axios.post(
        `${API}/billing/checkout`,
        {
          plan: 'Pro',
          origin_url: window.location.origin
        },
        { headers: getAuthHeaders() }
      );
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      const message = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(message);
      setUpgrading(false);
    }
  };
  
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/profile`, profile, { headers: getAuthHeaders() });
      await refreshUser();
      toast.success('Profile updated!');
    } catch (error) {
      console.error('Save profile error:', error);
      toast.error('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };
  
  const handleImageUpload = async (e, imageType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Maximum 5MB.');
      return;
    }
    
    setUploadingImage(imageType);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/profile/upload-image?image_type=${imageType}`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (imageType === 'headshot') {
        setHeadshot(response.data.url);
      } else {
        setBrokerageLogo(response.data.url);
      }
      
      await refreshUser();
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };

  const trialDaysRemaining = () => {
    if (user?.plan !== 'Trial') return null;
    const trialEnd = new Date(user.trial_end_date);
    const now = new Date();
    const days = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary/30">
        <Navbar variant="app" />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="account-page">
      <Navbar variant="app" />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-heading font-bold text-foreground mb-8">Account</h1>

        {/* Current Plan */}
        <Card className="mb-6 border-border/40">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl font-bold text-foreground">{user?.plan}</span>
                  {user?.plan === 'Free' && (
                    <Badge variant="secondary" className="bg-accent/10 text-accent">
                      Limited
                    </Badge>
                  )}
                  {user?.plan === 'Pro' && (
                    <Badge variant="secondary" className="bg-green-500/10 text-green-600">
                      Lifetime Access
                    </Badge>
                  )}
                </div>
                
                {user?.plan === 'Free' && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span>Upgrade to Pro for unlimited listings</span>
                  </div>
                )}
                
                {user?.plan === 'Pro' && (
                  <p className="text-muted-foreground">
                    Lifetime Access • Started {formatDate(user?.plan_start_date)}
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  {usage.active_listings} / {usage.listing_limit === -1 || usage.listing_limit >= 999 ? 'Unlimited' : usage.listing_limit} active listings
                </span>
                {usage.listing_limit < 999 && (
                  <Progress 
                    value={(usage.active_listings / usage.listing_limit) * 100} 
                    className="w-24 h-2" 
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upgrade Option - Only show for Free users */}
        {user?.plan === 'Free' && (
          <Card className="mb-6 border-border/40 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4">
              <div className="flex items-center gap-2">
                <Sparkles size={20} />
                <span className="font-semibold">Upgrade to ListingPro AI</span>
              </div>
            </div>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold text-foreground">$20</span>
                    <span className="text-muted-foreground">one-time</span>
                  </div>
                  <p className="text-sm text-green-600 font-medium mb-3">Lifetime access - pay once, use forever!</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle size={14} className="text-green-500" />
                      Unlimited active listings
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={14} className="text-green-500" />
                      All features included
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={14} className="text-green-500" />
                      Priority support
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle size={14} className="text-green-500" />
                      Cancel anytime
                    </li>
                  </ul>
                </div>
                <Button
                  onClick={handleUpgrade}
                  disabled={upgrading}
                  size="lg"
                  className="rounded-full bg-accent hover:bg-accent/90 px-8"
                  data-testid="upgrade-pro-btn"
                >
                  {upgrading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Processing...
                    </>
                  ) : (
                    'Upgrade Now'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agent Profile */}
        <Card className="mb-6 border-border/40">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <User className="h-5 w-5" />
              Agent Profile
            </CardTitle>
            <CardDescription>
              Your profile information appears on market updates and marketing materials
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Profile Images */}
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Headshot */}
              <div className="flex-1">
                <Label className="text-sm font-medium mb-2 block">Headshot Photo</Label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {headshot ? (
                      <img 
                        src={headshot} 
                        alt="Headshot" 
                        className="w-20 h-20 rounded-full object-cover border-2 border-border"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center border-2 border-dashed border-border">
                        <User className="text-muted-foreground" size={32} />
                      </div>
                    )}
                    <button
                      onClick={() => headshotInputRef.current?.click()}
                      disabled={uploadingImage === 'headshot'}
                      className="absolute -bottom-1 -right-1 p-1.5 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
                    >
                      {uploadingImage === 'headshot' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Camera size={14} />
                      )}
                    </button>
                  </div>
                  <input
                    ref={headshotInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'headshot')}
                    className="hidden"
                  />
                  <p className="text-xs text-muted-foreground">
                    Square photo works best.<br />Max 5MB.
                  </p>
                </div>
              </div>
              
              {/* Brokerage Logo */}
              <div className="flex-1">
                <Label className="text-sm font-medium mb-2 block">Brokerage Logo</Label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {brokerageLogo ? (
                      <img 
                        src={brokerageLogo} 
                        alt="Brokerage Logo" 
                        className="w-20 h-16 object-contain border rounded-lg bg-white p-1"
                      />
                    ) : (
                      <div className="w-20 h-16 rounded-lg bg-secondary flex items-center justify-center border-2 border-dashed border-border">
                        <Building2 className="text-muted-foreground" size={24} />
                      </div>
                    )}
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingImage === 'brokerage_logo'}
                      className="absolute -bottom-1 -right-1 p-1.5 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors"
                    >
                      {uploadingImage === 'brokerage_logo' ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Camera size={14} />
                      )}
                    </button>
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'brokerage_logo')}
                    className="hidden"
                  />
                  <p className="text-xs text-muted-foreground">
                    PNG with transparency works best.<br />Max 5MB.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Profile Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={profile.name}
                  onChange={(e) => setProfile({...profile, name: e.target.value})}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={profile.phone}
                  onChange={(e) => setProfile({...profile, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="brokerage">Brokerage Name</Label>
                <Input
                  id="brokerage"
                  value={profile.brokerage_name}
                  onChange={(e) => setProfile({...profile, brokerage_name: e.target.value})}
                  placeholder="ABC Realty"
                />
              </div>
            </div>
            
            {/* Social Links */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Social Media Links</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Facebook size={18} className="text-blue-600 flex-shrink-0" />
                  <Input
                    value={profile.social_facebook}
                    onChange={(e) => setProfile({...profile, social_facebook: e.target.value})}
                    placeholder="Facebook profile URL"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Instagram size={18} className="text-pink-600 flex-shrink-0" />
                  <Input
                    value={profile.social_instagram}
                    onChange={(e) => setProfile({...profile, social_instagram: e.target.value})}
                    placeholder="Instagram profile URL"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Linkedin size={18} className="text-blue-700 flex-shrink-0" />
                  <Input
                    value={profile.social_linkedin}
                    onChange={(e) => setProfile({...profile, social_linkedin: e.target.value})}
                    placeholder="LinkedIn profile URL"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Twitter size={18} className="text-sky-500 flex-shrink-0" />
                  <Input
                    value={profile.social_twitter}
                    onChange={(e) => setProfile({...profile, social_twitter: e.target.value})}
                    placeholder="Twitter/X profile URL"
                  />
                </div>
              </div>
            </div>
            
            <Button 
              onClick={handleSaveProfile} 
              disabled={savingProfile}
              className="gap-2"
            >
              {savingProfile ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Profile
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Billing History */}
        <Card className="border-border/40">
          <CardHeader>
            <CardTitle className="font-heading">Billing History</CardTitle>
          </CardHeader>
          <CardContent>
            {billingHistory.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No billing history yet</p>
            ) : (
              <div className="space-y-4">
                {billingHistory.map((transaction) => (
                  <div 
                    key={transaction.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-secondary/30"
                  >
                    <div>
                      <p className="font-medium">ListingPro AI</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(transaction.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${transaction.amount}</p>
                      <Badge variant={transaction.payment_status === 'paid' ? 'default' : 'secondary'}>
                        {transaction.payment_status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Account;
